import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Edit, Trash2, Download, FileText, X, Save, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComercialClientsTab() {
  const [clients, setClients] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingClient, setEditingClient] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/clients`),  // TODOS los clientes (3 negocios)
      axios.get(`${API}/quotations?business_id=comercializadora`),
    ])
      .then(([cRes, qRes]) => {
        const all = cRes.data?.clients || cRes.data || [];
        // Ordenar priorizando los de comercializadora primero
        const ordered = [...all].sort((a, b) => {
          const aIsCom = a.business_id === "comercializadora" ? 0 : 1;
          const bIsCom = b.business_id === "comercializadora" ? 0 : 1;
          return aIsCom - bIsCom;
        });
        setClients(ordered);
        setQuotations(qRes.data?.quotations || qRes.data || []);
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  // Calcular total facturado por cliente en el año actual
  const currentYear = new Date().getFullYear();
  const statsByClient = useMemo(() => {
    const map = {};
    quotations.forEach((q) => {
      const key = (q.client_company || q.client_name || "").toLowerCase().trim();
      if (!key) return;
      const yr = new Date(q.created_at || q.date || 0).getFullYear();
      if (yr !== currentYear) return;
      if (!map[key]) map[key] = { count: 0, total: 0 };
      map[key].count++;
      map[key].total += parseFloat(q.total) || 0;
    });
    return map;
  }, [quotations, currentYear]);

  const filteredClients = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return clients;
    return clients.filter(
      (c) =>
        (c.business_name || "").toLowerCase().includes(q) ||
        (c.rfc || "").toLowerCase().includes(q) ||
        (c.primary_contact?.name || "").toLowerCase().includes(q) ||
        (c.primary_contact?.email || "").toLowerCase().includes(q) ||
        (c.primary_contact?.phone || "").toLowerCase().includes(q)
    );
  }, [clients, search]);

  const clientQuotations = useMemo(() => {
    if (!selectedClient) return [];
    const name = (selectedClient.business_name || "").toLowerCase().trim();
    return quotations.filter(
      (q) =>
        (q.client_company || "").toLowerCase().trim() === name ||
        (q.client_name || "").toLowerCase().trim() === name
    );
  }, [selectedClient, quotations]);

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/clients/${editingClient.id}`, {
        business_name: editingClient.business_name,
        rfc: editingClient.rfc,
        address: editingClient.address || {},
        primary_contact: editingClient.primary_contact || {},
      });
      setClients((prev) =>
        prev.map((c) => (c.id === editingClient.id ? editingClient : c))
      );
      toast.success("Cliente actualizado");
      setEditingClient(null);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este cliente? No afecta las cotizaciones ya guardadas.")) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      setClients((prev) => prev.filter((c) => c.id !== id));
      if (selectedClient?.id === id) setSelectedClient(null);
      toast.success("Cliente eliminado");
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const exportExcel = () => {
    // CSV simple
    const header = ["Empresa", "RFC", "Contacto", "Email", "Teléfono", "Dirección", "Cotizaciones (año)", "Total facturado (año)"];
    const rows = filteredClients.map((c) => {
      const k = (c.business_name || "").toLowerCase().trim();
      const s = statsByClient[k] || { count: 0, total: 0 };
      return [
        c.business_name || "",
        c.rfc || "",
        c.primary_contact?.name || "",
        c.primary_contact?.email || "",
        c.primary_contact?.phone || "",
        c.address?.street || "",
        s.count,
        s.total.toFixed(2),
      ];
    });
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clientes-comercializadora-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatMx = (v) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);

  if (loading) {
    return <div className="text-slate-400 p-8 text-center">Cargando clientes...</div>;
  }

  return (
    <div className="space-y-6" data-testid="comercial-clients-tab">
      {/* Header con búsqueda y export */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-xl font-semibold text-white flex items-center gap-2">
            <User className="w-5 h-5" />
            Clientes ({filteredClients.length})
          </h3>
          <p className="text-slate-400 text-sm">
            Clientes guardados desde Comercializadora — total {currentYear}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, RFC, email..."
              data-testid="comercial-clients-search"
              className="pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm w-72"
            />
          </div>
          <button
            onClick={exportExcel}
            data-testid="comercial-clients-export-btn"
            className="px-3 py-2 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-300 rounded-lg text-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Tabla de clientes */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-900/80 text-xs uppercase text-slate-400">
            <tr>
              <th className="text-left px-4 py-3">Empresa / Cliente</th>
              <th className="text-left px-4 py-3">RFC</th>
              <th className="text-left px-4 py-3">Contacto</th>
              <th className="text-right px-4 py-3">Cotizaciones {currentYear}</th>
              <th className="text-right px-4 py-3">Total {currentYear}</th>
              <th className="text-right px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {filteredClients.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-12 text-slate-500">
                  {search
                    ? "Sin resultados"
                    : "Aún no hay clientes guardados. Usa el botón “Guardar como cliente recurrente” en una cotización."}
                </td>
              </tr>
            )}
            {filteredClients.map((c) => {
              const k = (c.business_name || "").toLowerCase().trim();
              const s = statsByClient[k] || { count: 0, total: 0 };
              return (
                <tr
                  key={c.id}
                  className="hover:bg-slate-800/40 transition cursor-pointer"
                  onClick={() => setSelectedClient(c)}
                  data-testid={`comercial-client-row-${c.id}`}
                >
                  <td className="px-4 py-3 text-white text-sm font-medium">
                    {c.business_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-xs">{c.rfc || "—"}</td>
                  <td className="px-4 py-3 text-slate-300 text-xs">
                    {c.primary_contact?.name || "—"}
                    {c.primary_contact?.phone && (
                      <span className="text-slate-500 block">{c.primary_contact.phone}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-white text-sm">{s.count}</td>
                  <td className="px-4 py-3 text-right text-emerald-400 text-sm font-medium">
                    {formatMx(s.total)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingClient(JSON.parse(JSON.stringify(c)));
                        }}
                        className="p-1.5 hover:bg-slate-700 rounded text-blue-300"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(c.id);
                        }}
                        className="p-1.5 hover:bg-slate-700 rounded text-red-400"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Panel detalle: historial cotizaciones del cliente */}
      {selectedClient && !editingClient && (
        <div className="bg-slate-900/60 border border-emerald-500/30 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Historial — {selectedClient.business_name}
            </h4>
            <button
              onClick={() => setSelectedClient(null)}
              className="text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {clientQuotations.length === 0 ? (
            <p className="text-slate-500 text-sm">Sin cotizaciones aún para este cliente.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-auto">
              {clientQuotations.map((q) => (
                <div
                  key={q.id}
                  className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 flex items-center justify-between text-sm"
                >
                  <div>
                    <div className="text-white font-medium">{q.quotation_number}</div>
                    <div className="text-slate-400 text-xs">
                      {new Date(q.created_at || q.date).toLocaleDateString("es-MX")} ·{" "}
                      {q.status || "draft"}
                    </div>
                  </div>
                  <div className="text-emerald-400 font-medium">{formatMx(q.total)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal edición */}
      {editingClient && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-semibold">Editar cliente</h4>
              <button onClick={() => setEditingClient(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Empresa / Razón social" value={editingClient.business_name}
                onChange={(v) => setEditingClient({ ...editingClient, business_name: v })} />
              <Field label="RFC" value={editingClient.rfc}
                onChange={(v) => setEditingClient({ ...editingClient, rfc: v.toUpperCase() })} />
              <Field label="Contacto" value={editingClient.primary_contact?.name || ""}
                onChange={(v) =>
                  setEditingClient({
                    ...editingClient,
                    primary_contact: { ...(editingClient.primary_contact || {}), name: v },
                  })
                } />
              <Field label="Teléfono" value={editingClient.primary_contact?.phone || ""}
                onChange={(v) =>
                  setEditingClient({
                    ...editingClient,
                    primary_contact: { ...(editingClient.primary_contact || {}), phone: v },
                  })
                } />
              <Field label="Email" value={editingClient.primary_contact?.email || ""}
                onChange={(v) =>
                  setEditingClient({
                    ...editingClient,
                    primary_contact: { ...(editingClient.primary_contact || {}), email: v },
                  })
                } />
              <Field label="Cargo / ATN" value={editingClient.primary_contact?.position || ""}
                onChange={(v) =>
                  setEditingClient({
                    ...editingClient,
                    primary_contact: { ...(editingClient.primary_contact || {}), position: v },
                  })
                } />
              <div className="md:col-span-2">
                <Field label="Dirección" value={editingClient.address?.street || ""}
                  onChange={(v) =>
                    setEditingClient({
                      ...editingClient,
                      address: { ...(editingClient.address || {}), street: v },
                    })
                  } />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setEditingClient(null)}
                className="px-4 py-2 border border-slate-600 rounded text-slate-300 hover:bg-slate-800 text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                data-testid="comercial-client-save-btn"
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded text-white text-sm flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-white text-sm"
      />
    </div>
  );
}
